import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, FileText, Loader2, Send } from 'lucide-react';
import { Drawer } from './ui/Drawer';
import { Pill } from './ui/Pill';
import { useConfirm } from './ui/ConfirmDialog';
import {
  useArInvoice,
  useSendArInvoice,
  useRecordArPayment,
  useGenerateArPdf,
  useVoidArInvoice,
  openArInvoiceFile,
} from '../hooks/useArInvoices';
import { formatAmount, formatDate } from '../lib/format';
import { useCan } from '../hooks/useCan';

export function ArInvoiceDrawer({
  wsId,
  invoiceId,
  open,
  onOpenChange,
}: {
  wsId: string;
  invoiceId: string | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const { canMutate } = useCan();
  const confirm = useConfirm();
  const { data: inv, isLoading } = useArInvoice(wsId, invoiceId);
  const send = useSendArInvoice(wsId);
  const pay = useRecordArPayment(wsId);
  const genPdf = useGenerateArPdf(wsId);
  const voidInv = useVoidArInvoice(wsId);
  const [payAmount, setPayAmount] = useState('');
  const [payOpen, setPayOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const onSend = async () => {
    if (!inv) return;
    setActionError(null);
    try {
      await send.mutateAsync(inv.id);
    } catch (err: any) {
      setActionError(err?.response?.data?.message ?? err?.message ?? 'Send failed');
    }
  };

  const onRecord = async () => {
    if (!inv) return;
    const cents = Number(payAmount);
    if (!Number.isFinite(cents) || cents <= 0) {
      setActionError(t('invoicesAr.payInvalid'));
      return;
    }
    setActionError(null);
    try {
      await pay.mutateAsync({ id: inv.id, amountCents: cents });
      setPayAmount('');
      setPayOpen(false);
    } catch (err: any) {
      setActionError(err?.response?.data?.message ?? err?.message ?? 'Record payment failed');
    }
  };

  const onGeneratePdf = async () => {
    if (!inv) return;
    setActionError(null);
    try {
      await genPdf.mutateAsync(inv.id);
      await openArInvoiceFile(wsId, inv.id);
    } catch (err: any) {
      setActionError(err?.response?.data?.message ?? err?.message ?? 'PDF generation failed');
    }
  };

  const balanceCents = inv ? Number(inv.total_cents) - Number(inv.paid_cents) : 0;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={inv?.invoice_number ?? '—'}
      description={inv?.peppol_sent_at ? `Peppol ${formatDate(inv.peppol_sent_at, i18n.language)}` : undefined}
      footer={
        inv ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-4">
              {inv.pdf_url && (
                <button
                  type="button"
                  onClick={() => openArInvoiceFile(wsId, inv.id)}
                  className="inline-flex items-center gap-1.5 text-sm text-ink-600 hover:text-ink-900 dark:text-ink-300 dark:hover:text-ink-100"
                >
                  <ExternalLink size={14} />
                  {t('inbox.viewOriginal')}
                </button>
              )}
              <div className="text-xs text-ink-500">
                {t('invoicesAr.balance')}:{' '}
                <span className="num font-semibold">
                  {formatAmount(balanceCents, inv.currency, i18n.language)}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onGeneratePdf}
                disabled={genPdf.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900 disabled:opacity-50"
              >
                {genPdf.isPending ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                {t('invoicesAr.generatePdf')}
              </button>
              {canMutate &&
                (inv.status === 'draft' || inv.status === 'sent' || inv.status === 'partial' || inv.status === 'overdue') &&
                balanceCents > 0 && (
                  <button
                    type="button"
                    onClick={() => setPayOpen((o) => !o)}
                    className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-ink-50 dark:hover:bg-ink-900"
                  >
                    {t('invoicesAr.recordPayment')}
                  </button>
                )}
              {canMutate && inv.status !== 'paid' && inv.status !== 'void' && (
                <button
                  type="button"
                  onClick={async () => { if (await confirm({ title: t('inbox.confirmVoid'), confirmLabel: t('inbox.void'), destructive: true })) voidInv.mutate(inv.id); }}
                  disabled={voidInv.isPending}
                  className="px-3 py-1.5 rounded-lg border border-ink-300 dark:border-ink-700 text-xs font-medium hover:bg-warn-50 dark:hover:bg-warn-900/20 text-warn-600 disabled:opacity-50"
                >
                  {t('inbox.void')}
                </button>
              )}
              {canMutate && inv.status === 'draft' && (
                <button
                  type="button"
                  onClick={onSend}
                  disabled={send.isPending}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                >
                  <Send size={12} />
                  {t('invoicesAr.send')}
                </button>
              )}
            </div>
          </div>
        ) : null
      }
    >
      {isLoading || !inv ? (
        <div className="flex items-center gap-2 text-sm text-ink-500 py-10 justify-center">
          <Loader2 size={14} className="animate-spin" />
          {t('common.loading')}
        </div>
      ) : (
        <div className="space-y-6">
          {actionError && (
            <div className="text-sm text-warn-600 rounded-lg border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-3 py-2">
              {actionError}
            </div>
          )}

          {payOpen && (
            <section className="rounded-lg border border-ink-200 dark:border-ink-800 p-3 bg-ink-50 dark:bg-ink-900/50">
              <div className="text-xs uppercase tracking-wide text-ink-500 mb-2">
                {t('invoicesAr.recordPayment')}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder={t('invoicesAr.amountInCents')}
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  className="flex-1 px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500"
                />
                <button
                  type="button"
                  onClick={onRecord}
                  disabled={pay.isPending}
                  className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                >
                  {t('common.save')}
                </button>
              </div>
            </section>
          )}

          <section className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
            <Field
              label={t('common.status')}
              value={
                <Pill
                  tone={
                    inv.status === 'paid'
                      ? 'success'
                      : inv.status === 'overdue'
                      ? 'warn'
                      : inv.status === 'partial'
                      ? 'accent'
                      : 'neutral'
                  }
                >
                  {t(`invoicesAr.status.${inv.status}`)}
                </Pill>
              }
            />
            <Field
              label={t('inbox.field.registration')}
              value={inv.jp_qualified ? <Pill tone="success">{t('vendors.qualifiedActive')}</Pill> : '—'}
            />
            <Field label={t('inbox.field.issueDate')} value={formatDate(inv.issue_date, i18n.language)} />
            <Field label={t('inbox.field.dueDate')} value={formatDate(inv.due_date, i18n.language)} />
            <Field label={t('inbox.field.currency')} value={inv.currency} />
            <Field
              label={t('inbox.field.subtotal')}
              value={formatAmount(inv.subtotal_cents, inv.currency, i18n.language)}
            />
            <Field
              label={t('inbox.field.tax')}
              value={formatAmount(inv.tax_cents, inv.currency, i18n.language)}
            />
            <Field
              label={t('inbox.field.total')}
              value={
                <span className="num text-base font-semibold">
                  {formatAmount(inv.total_cents, inv.currency, i18n.language)}
                </span>
              }
            />
            <Field
              label={t('invoicesAr.paid')}
              value={formatAmount(inv.paid_cents, inv.currency, i18n.language)}
            />
            <Field
              label={t('invoicesAr.peppolColumn')}
              value={inv.peppol_sent_at ? formatDate(inv.peppol_sent_at, i18n.language) : '—'}
            />
          </section>

          {inv.line_items && inv.line_items.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wide text-ink-500 mb-2">
                {t('inbox.lineItems')}
              </h3>
              <div className="rounded-lg border border-ink-200 dark:border-ink-800 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-ink-50 dark:bg-ink-900/50 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">{t('inbox.col.description')}</th>
                      <th className="px-3 py-2 font-medium text-right">{t('inbox.col.qty')}</th>
                      <th className="px-3 py-2 font-medium text-right">{t('inbox.col.unit')}</th>
                      <th className="px-3 py-2 font-medium text-right">{t('invoicesAr.taxRate')}</th>
                      <th className="px-3 py-2 font-medium text-right">{t('inbox.col.amount')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
                    {inv.line_items.map((li, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{li.description}</td>
                        <td className="px-3 py-2 text-right num">{li.qty}</td>
                        <td className="px-3 py-2 text-right num">
                          {formatAmount(li.unitPriceCents, inv.currency, i18n.language)}
                        </td>
                        <td className="px-3 py-2 text-right num">{li.taxRate}%</td>
                        <td className="px-3 py-2 text-right num">
                          {formatAmount(
                            Math.round(li.unitPriceCents * li.qty),
                            inv.currency,
                            i18n.language,
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}
    </Drawer>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-ink-500">{label}</div>
      <div className="mt-0.5 truncate">{value}</div>
    </div>
  );
}
